import * as React from 'react';
import styled from 'styled-components';
import { Layout } from 'antd';
import { Content as VContent } from '../Layout';
import { AI2Banner } from './AI2Banner';
import { useSmartAI2Banner } from './useSmartAI2Banner';
// This is an alias we create so that the user doesn't have to mix abstraction
// levels when implemnting something more custom. This way the user can do everything
// with the <Header.*> components, and doesn't need to use the antd ones.
const Container = Layout.Header;
const Content = styled(VContent) `
    display: flex;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
`;
const Sticky = styled.div `
    && {
        position: sticky;
        top: 0;
        z-index: ${({ theme }) => theme.zIndex.header};
        width: 100%;
        box-shadow: ${({ theme }) => theme.shadows.header};
        transition: top 200ms ease-in-out;
    }

    .ant-layout-header {
        height: initial;
        padding: 0;
        background: ${({ theme }) => theme.palette.common.white.toString()};
        line-height: initial;
    }
`;
const AppName = styled.h5 `
    margin: 0;
    padding: 0;
    font-size: 1.9rem;
    line-height: 1.65rem;
    text-transform: none;
    display: inline-block;

    @media (max-width: ${({ theme }) => theme.breakpoints.xs.toString()}) {
        font-size: 1.75rem;
        line-height: 1.5rem;
    }
`;
const AppTagline = styled.h6 `
    margin: 0;
    padding: 0;
    font-size: 1.5rem;
    line-height: 1.65rem;
    text-transform: none;
    display: inline-block;

    @media (max-width: ${({ theme }) => theme.breakpoints.xs.toString()}) {
        font-size: 1.375rem;
        line-height: 1.5rem;
    }
`;
const LogoGrid = styled.span `
    display: grid;
    grid-template-columns: repeat(4, auto);
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs.toString()};

    &:hover ${AppName} {
        text-decoration: none;
        display: inline-block;
    }
`;
// Wraps the header logo with an ahref if href is passed in
const ConditionalAnchorWrapper = (props) => {
    return props.href ? (React.createElement(DecorationlessAnchor, { href: props.href, rel: "noopener noreferrer" }, props.children)) : (props.children);
};
const DecorationlessAnchor = styled.a `
    &&:hover {
        text-decoration: none;
    }
`;
const Logo = (props) => {
    return (React.createElement(ConditionalAnchorWrapper, { href: props.href },
        React.createElement(LogoGrid, null,
            props.children,
            props.label,
            props.label && props.tagline ? (props.separator ? (props.separator) : (React.createElement(AppTagline, null, "\u2014"))) : null,
            props.tagline)));
};
const Columns = styled.div `
    display: grid;
    grid-template-columns: ${({ columns }) => columns};
    grid-column-gap: ${({ theme }) => theme.spacing.md.toString()};
    grid-row-gap: ${({ theme }) => theme.spacing.md.toString()};
    width: 100%;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.sm.toString()} 0;
`;
const MenuColumn = styled.div `
    &&&& {
        justify-self: right;
        line-height: 77px;
        a:hover {
            text-decoration: none;
        }
        ul {
            margin: -${({ theme }) => theme.spacing.sm.toString()} 0;
        }
    }
`;
function HeaderComponent({ alwaysVisible, children }) {
    const [sticky, banner, offsetTop] = useSmartAI2Banner();
    return (React.createElement(Sticky, { ref: sticky, style: { top: `${!alwaysVisible ? offsetTop : 0}px` } },
        React.createElement(Layout.Header, null,
            React.createElement(AI2Banner, { ref: banner }),
            React.createElement(Content, null, children))));
}
/**
 * Merge in the components that are tightly coupled to the <Header>,
 * so that they can be used like:
 *  <Header>
 *      <Header.Columns>
 *          ...
 *      </Header.Columns>
 *  </Header>
 */
export const Header = Object.assign(HeaderComponent, {
    AI2Banner,
    AppName,
    AppTagline,
    Columns,
    Content,
    Container,
    Logo,
    MenuColumn,
    Sticky,
});
//# sourceMappingURL=Header.js.map